import { useState } from 'react'
import { GitCompare, ChevronDown, Clock, Sparkles, Pencil } from 'lucide-react'
import { MarkdownRenderer } from './MarkdownRenderer'
import { CritiquePanel } from './CritiquePanel'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { motion, AnimatePresence } from 'framer-motion'

interface ContentVersion {
  id: string
  version: number
  body: string
  critique_score: number | null
  critique_feedback: Record<string, unknown> | null
  created_at: string
  edited_with_ai?: boolean
}

interface VersionCompareProps {
  versions: ContentVersion[]
  isLoading?: boolean
}

/**
 * Simple text diff that highlights differences between two strings.
 * Returns an array of segments with added/removed/same status.
 */
function computeSimpleDiff(
  textA: string,
  textB: string
): Array<{ type: 'same' | 'added' | 'removed'; text: string }> {
  const wordsA = textA.split(/(\s+)/)
  const wordsB = textB.split(/(\s+)/)
  const result: Array<{ type: 'same' | 'added' | 'removed'; text: string }> = []

  let i = 0
  let j = 0

  while (i < wordsA.length && j < wordsB.length) {
    if (wordsA[i] === wordsB[j]) {
      result.push({ type: 'same', text: wordsA[i] })
      i++
      j++
    } else {
      // Look ahead to find next match
      let foundA = -1
      let foundB = -1

      for (let k = j + 1; k < Math.min(j + 20, wordsB.length); k++) {
        if (wordsA[i] === wordsB[k]) {
          foundB = k
          break
        }
      }

      for (let k = i + 1; k < Math.min(i + 20, wordsA.length); k++) {
        if (wordsA[k] === wordsB[j]) {
          foundA = k
          break
        }
      }

      if (foundB !== -1 && (foundA === -1 || foundB - j <= foundA - i)) {
        // Words were added in B
        for (let k = j; k < foundB; k++) {
          result.push({ type: 'added', text: wordsB[k] })
        }
        j = foundB
      } else if (foundA !== -1) {
        // Words were removed from A
        for (let k = i; k < foundA; k++) {
          result.push({ type: 'removed', text: wordsA[k] })
        }
        i = foundA
      } else {
        result.push({ type: 'removed', text: wordsA[i] })
        result.push({ type: 'added', text: wordsB[j] })
        i++
        j++
      }
    }
  }

  // Remaining words
  while (i < wordsA.length) {
    result.push({ type: 'removed', text: wordsA[i] })
    i++
  }
  while (j < wordsB.length) {
    result.push({ type: 'added', text: wordsB[j] })
    j++
  }

  return result
}

function DiffView({ versionA, versionB }: { versionA: ContentVersion; versionB: ContentVersion }) {
  const diff = computeSimpleDiff(versionA.body, versionB.body)

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-2 divide-x divide-slate-200 bg-slate-50 text-xs font-medium text-slate-600 px-4 py-2">
        <span>Versión {versionA.version} (anterior)</span>
        <span className="pl-4">Versión {versionB.version} (nueva)</span>
      </div>
      <div className="p-4 text-sm leading-relaxed">
        <div className="whitespace-pre-wrap">
          {diff.map((segment, i) => {
            if (segment.type === 'same') {
              return <span key={i}>{segment.text}</span>
            }
            if (segment.type === 'removed') {
              return (
                <span
                  key={i}
                  className="bg-red-100 text-red-800 line-through decoration-red-400"
                >
                  {segment.text}
                </span>
              )
            }
            return (
              <span key={i} className="bg-emerald-100 text-emerald-800">
                {segment.text}
              </span>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function VersionCompare({ versions, isLoading }: VersionCompareProps) {
  const [mode, setMode] = useState<'list' | 'compare'>('list')
  const [versionA, setVersionA] = useState<string>('')
  const [versionB, setVersionB] = useState<string>('')
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  if (!versions || versions.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400">
        <GitCompare className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">Sin historial de versiones</p>
        <p className="text-xs mt-1 text-slate-400/90">
          Las versiones aparecerán al regenerar o editar este contenido
        </p>
      </div>
    )
  }

  const sortedVersions = [...versions].sort((a, b) => b.version - a.version)
  const versionAData = sortedVersions.find((v) => v.id === versionA)
  const versionBData = sortedVersions.find((v) => v.id === versionB)

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      {versions.length > 1 && (
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setMode('list')}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              mode === 'list'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Lista
          </button>
          <button
            onClick={() => setMode('compare')}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              mode === 'compare'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Comparar
          </button>
        </div>
      )}

      {mode === 'compare' && versions.length > 1 ? (
        <div className="space-y-3">
          {/* Version selectors */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Versión A</label>
              <select
                value={versionA}
                onChange={(e) => setVersionA(e.target.value)}
                className="input text-sm"
              >
                <option value="">Seleccionar...</option>
                {sortedVersions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version}
                    {v.edited_with_ai ? ' (Editado con IA)' : ' (Manual)'}
                    {v.critique_score ? ` (${v.critique_score}/10)` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Versión B</label>
              <select
                value={versionB}
                onChange={(e) => setVersionB(e.target.value)}
                className="input text-sm"
              >
                <option value="">Seleccionar...</option>
                {sortedVersions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version}
                    {v.edited_with_ai ? ' (Editado con IA)' : ' (Manual)'}
                    {v.critique_score ? ` (${v.critique_score}/10)` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Diff view */}
          {versionAData && versionBData && (
            <DiffView versionA={versionAData} versionB={versionBData} />
          )}

          {/* Score comparison */}
          {versionAData?.critique_score != null && versionBData?.critique_score != null && (
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">v{versionAData.version}</p>
                <p className="text-lg font-bold text-slate-700">
                  {versionAData.critique_score}/10
                </p>
              </div>
              <div className="text-center bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-1">v{versionBData.version}</p>
                <p className="text-lg font-bold text-slate-700">
                  {versionBData.critique_score}/10
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* List mode */
        <div className="space-y-2">
          {sortedVersions.map((version) => (
            <div key={version.id} className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() =>
                  setExpandedVersion(expandedVersion === version.id ? null : version.id)
                }
                className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-slate-700">
                    v{version.version}
                  </span>
                  {version.edited_with_ai ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"
                      title="Editado con IA"
                    >
                      <Sparkles className="w-3 h-3" />
                      Editado con IA
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
                      title="Editado manualmente"
                    >
                      <Pencil className="w-3 h-3" />
                      Editado manualmente
                    </span>
                  )}
                  {version.critique_score != null && (
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        version.critique_score >= 8
                          ? 'bg-emerald-100 text-emerald-700'
                          : version.critique_score >= 6
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {version.critique_score}/10
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(version.created_at), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform ${
                      expandedVersion === version.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </button>

              <AnimatePresence>
                {expandedVersion === version.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                      {/* Critique feedback */}
                      {version.critique_feedback && (
                        <div className="mt-3">
                          <CritiquePanel
                            score={version.critique_score}
                            feedback={version.critique_feedback}
                            compact
                          />
                        </div>
                      )}

                      {/* Body preview */}
                      <div className="mt-3 max-h-[300px] overflow-y-auto bg-slate-50 rounded-lg p-3">
                        <MarkdownRenderer content={version.body} className="text-sm" />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
